import { useState } from "react";
import { Link } from "react-router";
import type { PricingVersion } from "@shared-core/data/pricing-admin-types";
import { Button } from "../primitives/button";
import { Badge } from "../primitives/badge";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../primitives/alert-dialog";
import {
  SupersededList,
  liveVersion,
  supersededVersions,
} from "./version-history";
import { ArrowLeft, History } from "lucide-react";

// The "what am I editing" header, shared by the price-group and catalogue
// editors: a back link to the index, the entity name + scope, the live-version
// status line, an expandable revert history, and (when editable) the
// Save/Revert controls — kept up here in the banner, clear of the floating
// feedback button. Save asks "this goes live now?" on every scope.

export function EditorHeader({
  backTo,
  backLabel,
  name,
  subtitle,
  versions,
  actions,
  dirty = false,
  saving = false,
  onSave,
  onRevert,
}: {
  backTo: string;
  backLabel: string;
  name: string;
  subtitle: string;
  versions: PricingVersion[];
  /** Extra right-side actions (e.g. New item / Import). */
  actions?: React.ReactNode;
  dirty?: boolean;
  /** True while a save is in flight — keeps the Save/Revert block visible
   *  (disabled, "Saving…") instead of the caller hiding it via `onSave`. */
  saving?: boolean;
  onSave?: () => void;
  onRevert?: () => void;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const live = liveVersion(versions);
  const hasHistory = supersededVersions(versions).length > 0;

  return (
    <div className="space-y-3">
      <Link
        to={backTo}
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="size-4" /> {backLabel}
      </Link>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <h1>{name}</h1>
            {live && <Badge variant="success">Live · v{live.v}</Badge>}
            {hasHistory && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory((h) => !h)}
              >
                <History className="size-4" /> History
              </Button>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          {onSave && (
            <>
              {dirty && !saving && (
                <span className="text-sm text-muted-foreground">
                  Unsaved changes
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                disabled={!dirty || saving}
                onClick={onRevert}
              >
                Revert
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" disabled={!dirty || saving}>
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Save changes — this goes live now?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Saving commits a new live version and replaces the current
                      one straight away. The previous version is kept in
                      history, so you can revert at any time.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onSave}>
                      Save &amp; go live
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>
      {showHistory && hasHistory && (
        <div className="rounded-md border p-3">
          <SupersededList versions={versions} />
        </div>
      )}
    </div>
  );
}
